/**
 * PLAT-11207 — write an AI Prompt Log record for a server-composed prompt run.
 *
 * The AI Prompt Log is written client-side, by whoever made the call
 * (`ChatGPTService`, `CaptionService`). Everything that runs through
 * `/api/aiprompts/prompt-generate` (PLAT-10914) never opted in, so Section and
 * Chapter naming, reel chapterization and Smart Graphics AI text have been
 * generating without leaving a trace. This is that opt-in, written once and
 * shared, rather than a fourth copy of the same six lines (DS1/DS2).
 *
 * It is a hook because the record needs what only the stores hold — the DatoCMS
 * client, the person record, the signed-in identity — and a repository has no
 * business acquiring any of it.
 *
 * Deliberately unable to break a generation (DS3): no provider, no initialised
 * Dato client, or a save that fails all mean "no record", never a failed run.
 * `logPromptRun` always rethrows what `run` threw, and returns exactly what it
 * resolved.
 */
import { AIPromptLogger } from '@src/monitoring/utils/AIPromptLogger';
// `RootStoreContext` rather than `StoreContext`, which also *builds* a store —
// importing that would pull the whole store graph into every caller.
import { StoreContext } from '@src/stores-v2/RootStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useContext } from 'react';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type { RootStore } from '@src/stores-v2/RootStore';

const logger = LogManager.getLogger('monitoring:useAIPromptRunLog');

export type AIPromptRunDescriptor = {
  /**
   * Absent only where the caller cannot know it before the endpoint answers —
   * a Smart Graphics control carries a prompt id and nothing else (DS4).
   * `describePrompt` fills it in mid-run.
   */
  promptTitle?: string;
  promptType?: string;
  /** DatoCMS `ai_prompt` record id, linked on the log record. */
  aiPromptId?: string;
  /** The API route that ran the prompt, for the record's metadata. */
  endpoint: string;
  storyId?: string;
  showcaseId?: string;
  clipId?: string;
  metadata?: Record<string, unknown>;
};

/** Name the prompt once the response says which record ran (DS4). */
export type DescribePrompt = (meta: {
  promptId?: string;
  promptTitle?: string;
  promptType?: string;
}) => void;

export type LogAIPromptRun = <T>(
  descriptor: AIPromptRunDescriptor,
  run: (describePrompt: DescribePrompt) => Promise<T>,
) => Promise<T>;

/**
 * The title a record carries when the run failed before the endpoint named its
 * prompt. The `ai_prompt` link still points at the right record, so the entry
 * is identifiable; only the human-readable title is unknown.
 */
const UNNAMED_PROMPT_TITLE = 'AI Prompt';

export function useAIPromptRunLog(): LogAIPromptRun {
  // `useContext` rather than `useStore()`, which throws outside a provider —
  // the surfaces that call a prompt must not depend on one being there (DS3).
  const rootStore = useContext(StoreContext);

  return useCallback(
    async <T>(
      descriptor: AIPromptRunDescriptor,
      run: (describePrompt: DescribePrompt) => Promise<T>,
    ): Promise<T> => {
      const client = resolveDatoClient(rootStore);
      if (!client || !rootStore) {
        return run(() => {});
      }

      const identity = rootStore.userIdentityStore.currentIdentity;
      const promptLog = AIPromptLogger.start(client, {
        prompt_title: descriptor.promptTitle ?? UNNAMED_PROMPT_TITLE,
        prompt_type: descriptor.promptType ?? '',
        ...(descriptor.aiPromptId && { aiPromptId: descriptor.aiPromptId }),
        personId: rootStore.datoPersonStore.person?.id,
        storyId: descriptor.storyId,
        showcaseId: descriptor.showcaseId,
        metadata: {
          user_email: identity?.email,
          user_id: identity?.id,
          dato_environment: resolveEnvironment(rootStore),
          endpoint: descriptor.endpoint,
          ...(descriptor.clipId && { clip_id: descriptor.clipId }),
          ...descriptor.metadata,
        },
      });

      const describePrompt: DescribePrompt = meta =>
        promptLog.describePrompt({
          ...(meta.promptTitle && { prompt_title: meta.promptTitle }),
          ...(meta.promptType && { prompt_type: meta.promptType }),
          ...(meta.promptId && { aiPromptId: meta.promptId }),
        });

      try {
        const value = await run(describePrompt);
        promptLog.complete();
        return value;
      } catch (error) {
        promptLog.fail(error);
        throw error;
      }
    },
    [rootStore],
  );
}

/**
 * Both accessors throw when the store has not been initialised yet — which is
 * an ordinary state on a surface opened before sign-in resolved, not an error
 * worth failing a generation over.
 */
function resolveDatoClient(
  rootStore: RootStore | null,
): Client | ApiClient | undefined {
  if (!rootStore) return undefined;
  try {
    return rootStore.datoClientStore.datoClient;
  } catch {
    logger.debug('No Dato client yet; skipping the AI prompt log record');
    return undefined;
  }
}

function resolveEnvironment(rootStore: RootStore): string | undefined {
  try {
    return rootStore.datoClientStore.environment;
  } catch {
    return undefined;
  }
}
